/* ==========================================================================
   TressAllure (TA) · Release theme — Product Video (zoom) · FINAL
   Types: 'vimeo' (SDK, controls=0), 'embed' (YouTube, controls=0 + postMessage),
   'file' (<video> without native chrome). Custom tap/pause + sound toggle.

   Hardened lifecycle (orphan-proof):
   - On EVERY connect: the modal source (inline render, or the copy left in
     <body>) is deep-cloned into a virgin node, scrubbed (no iframe, no src,
     hidden), ALL other PvgModal-* copies for this id are purged, and every
     listener is bound exactly once on the fresh clone. No double-binding,
     no orphaned players surviving editor saves / theme re-renders.
   ========================================================================== */

(function () {
  'use strict';

  if (!window.customElements || customElements.get('product-video-gallery')) return;

  var VIMEO_SDK = 'https://player.vimeo.com/api/player.js';
  var DESKTOP_MQ = '(min-width: 750px)';
  var sdkPromise = null;

  function loadVimeoSdk() {
    if (window.Vimeo && window.Vimeo.Player) return Promise.resolve();
    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise(function (resolve, reject) {
      var script = document.createElement('script');
      script.src = VIMEO_SDK;
      script.async = true;
      script.onload = resolve;
      script.onerror = function () {
        sdkPromise = null;
        reject(new Error('Vimeo SDK failed to load'));
      };
      document.head.appendChild(script);
    });

    return sdkPromise;
  }

  function buildVimeoUrl(id, hash, muted) {
    var url =
      'https://player.vimeo.com/video/' +
      encodeURIComponent(id) +
      '?autoplay=1&playsinline=1&title=0&byline=0&portrait=0&controls=0';

    if (hash) url += '&h=' + encodeURIComponent(hash);
    if (muted) url += '&muted=1';

    return url;
  }

  /** Append autoplay params to a prebuilt embed URL (YouTube, clean UI). */
  function buildGenericEmbedUrl(src, muted) {
    var sep = src.indexOf('?') > -1 ? '&' : '?';
    return (
      src + sep +
      'autoplay=1&playsinline=1&rel=0&modestbranding=1&controls=0&enablejsapi=1&mute=' +
      (muted ? '1' : '0')
    );
  }

  function formatDuration(seconds) {
    var total = Math.round(Number(seconds) || 0);
    if (total <= 0) return '';

    var hours = Math.floor(total / 3600);
    var mins = Math.floor((total % 3600) / 60);
    var secs = total % 60;
    var pad = function (n) {
      return n < 10 ? '0' + n : String(n);
    };

    return hours > 0 ? hours + ':' + pad(mins) + ':' + pad(secs) : mins + ':' + pad(secs);
  }

  function fetchVimeoMeta(canonicalUrl) {
    var cacheKey = 'pvg:' + canonicalUrl;

    try {
      var cached = sessionStorage.getItem(cacheKey);
      if (cached) return Promise.resolve(JSON.parse(cached));
    } catch (e) {
      /* private mode / storage disabled */
    }

    var endpoint =
      'https://vimeo.com/api/oembed.json?url=' + encodeURIComponent(canonicalUrl) + '&width=960';

    return fetch(endpoint)
      .then(function (response) {
        if (!response.ok) throw new Error('oEmbed ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var meta = {
          thumbnail_url: data.thumbnail_url || '',
          duration: data.duration || 0,
          title: data.title || ''
        };
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(meta));
        } catch (e) {
          /* noop */
        }
        return meta;
      });
  }

  class ProductVideoGallery extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = -1;
      this.isOpen = false;
      this.lastFocused = null;
      this.vimeoPlayer = null;
      this.embedPaused = false;
      this.isMutedNow = false;
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOutsideClick = this.handleOutsideClick.bind(this);
    }

    connectedCallback() {
      if (this.pvgReady) return;

      this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-pvg-card]'));
      if (!this.cards.length) return;

      // --- Resolve the modal source: inline render, or the copy in <body> ---
      var modalId = this.dataset.modalId || '';
      var source = this.querySelector('[data-pvg-modal]');
      if (!source && modalId) source = document.getElementById(modalId);
      if (!source) return;

      // --- Build a VIRGIN clone: no listeners, no leftover players ----------
      var modal = source.cloneNode(true);
      var leftoverEmbed = modal.querySelector('[data-pvg-embed]');
      if (leftoverEmbed) {
        leftoverEmbed.innerHTML = '';
        leftoverEmbed.hidden = true;
      }
      var leftoverVideo = modal.querySelector('[data-pvg-video]');
      if (leftoverVideo) {
        leftoverVideo.removeAttribute('src');
        leftoverVideo.hidden = true;
      }
      var leftoverError = modal.querySelector('[data-pvg-error]');
      if (leftoverError) leftoverError.hidden = true;
      modal.hidden = true;

      // --- Purge EVERY other copy of this modal anywhere in the document ----
      var purgeId = modal.id || modalId;
      if (purgeId) {
        var stale = document.querySelectorAll('[id^="' + purgeId + '"], .pvg-modal:not([id])');
        for (var s = 0; s < stale.length; s++) {
          if (stale[s].parentNode) stale[s].parentNode.removeChild(stale[s]);
        }
      } else if (source.parentNode) {
        source.parentNode.removeChild(source);
      }

      document.body.appendChild(modal);

      this.pvgReady = true;
      this.startMuted = this.dataset.startMuted === 'true';
      this.showDuration = this.dataset.showDuration === 'true';
      this.wantsDock = modal.classList.contains('pvg-modal--right');
      this.isMutedNow = this.startMuted;

      this.modal = modal;
      this.videoEl = modal.querySelector('[data-pvg-video]');
      this.embedEl = modal.querySelector('[data-pvg-embed]');
      this.errorEl = modal.querySelector('[data-pvg-error]');
      this.titleEl = modal.querySelector('[data-pvg-title]');
      this.prevBtn = modal.querySelector('[data-pvg-prev]');
      this.nextBtn = modal.querySelector('[data-pvg-next]');
      this.tapEl = modal.querySelector('[data-pvg-tap]');
      this.soundBtn = modal.querySelector('[data-pvg-sound]');
      this.soundOnEl = modal.querySelector('[data-pvg-sound-on]');
      this.soundOffEl = modal.querySelector('[data-pvg-sound-off]');
      this.pausedEl = modal.querySelector('[data-pvg-paused]');

      if (this.cards.length < 2) modal.classList.add('is-single');

      var self = this;

      this.cards.forEach(function (card, index) {
        if (card.dataset.pvgBound) return;
        card.dataset.pvgBound = '1';
        card.addEventListener('click', function (event) {
          event.stopPropagation();
          self.open(index);
        });
      });

      // Modal is a fresh clone every connect: safe to bind without guards.
      Array.prototype.forEach.call(modal.querySelectorAll('[data-pvg-close]'), function (btn) {
        btn.addEventListener('click', function () {
          self.close();
        });
      });

      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', function () {
          self.step(-1);
        });
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', function () {
          self.step(1);
        });
      }
      if (this.tapEl) {
        this.tapEl.addEventListener('click', function () {
          self.togglePlay();
        });
      }
      if (this.soundBtn) {
        this.soundBtn.addEventListener('click', function () {
          self.toggleSound();
        });
      }

      if (this.videoEl) {
        this.videoEl.addEventListener('ended', function () {
          if (self.cards.length > 1) self.step(1);
        });
        this.videoEl.addEventListener('error', function () {
          if (self.videoEl.hidden) return;
          self.showError('This video is temporarily unavailable.');
        });
        this.videoEl.addEventListener('volumechange', function () {
          self.setSoundIcon(self.videoEl.muted);
        });
        this.videoEl.addEventListener('pause', function () {
          if (!self.videoEl.hidden && self.pausedEl) self.pausedEl.hidden = false;
        });
        this.videoEl.addEventListener('play', function () {
          if (self.pausedEl) self.pausedEl.hidden = true;
        });
      }

      document.removeEventListener('keydown', this.handleKeydown);
      document.addEventListener('keydown', this.handleKeydown);

      this.hydrateThumbnails();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      document.removeEventListener('click', this.handleOutsideClick, true);
      this.stopPlayback();
      if (this.modal) this.modal.hidden = true;
      this.isOpen = false;
      document.body.classList.remove('pvg-no-scroll');
      this.pvgReady = false;
    }

    isDocked() {
      return this.wantsDock;
    }

    /* ------------------------------ Thumbnails --------------------------- */

    hydrateThumbnails() {
      if (this.dataset.fetchMeta !== 'true') {
        this.clearLoadingStates();
        return;
      }

      var self = this;
      var pending = this.cards.filter(function (card) {
        return !!card.dataset.oembed;
      });

      if (!pending.length) {
        this.clearLoadingStates();
        return;
      }

      pending.forEach(function (card) {
        fetchVimeoMeta(card.dataset.oembed)
          .then(function (meta) {
            self.applyMeta(card, meta);
          })
          .catch(function () {
            var wrap = card.querySelector('[data-pvg-thumb-wrap]');
            if (wrap) wrap.classList.remove('is-loading');
          });
      });
    }

    applyMeta(card, meta) {
      var wrap = card.querySelector('[data-pvg-thumb-wrap]');
      var img = card.querySelector('[data-pvg-thumb]');
      var durationEl = card.querySelector('[data-pvg-duration]');

      if (img && meta.thumbnail_url) {
        img.src = meta.thumbnail_url;
        img.hidden = false;
        if (!img.getAttribute('alt') && meta.title) img.setAttribute('alt', meta.title);
      }

      if (wrap) wrap.classList.remove('is-loading');

      if (this.showDuration && durationEl) {
        var label = formatDuration(meta.duration);
        if (label) {
          durationEl.textContent = label;
          durationEl.hidden = false;
        }
      }

      if (!card.dataset.title && meta.title) {
        card.dataset.title = meta.title;
        card.setAttribute('aria-label', 'Play video: ' + meta.title);
      }
    }

    clearLoadingStates() {
      this.cards.forEach(function (card) {
        var wrap = card.querySelector('[data-pvg-thumb-wrap]');
        if (wrap) wrap.classList.remove('is-loading');
      });
    }

    /* ------------------------------ Navigation --------------------------- */

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (this.cards.length < 2) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      }
    }

    handleOutsideClick(event) {
      if (!this.isOpen) return;
      if (this.modal.contains(event.target)) return;
      this.close();
    }

    step(delta) {
      var total = this.cards.length;
      var next = (this.currentIndex + delta + total) % total;
      this.load(next);
    }

    open(index) {
      if (!this.isOpen) {
        this.lastFocused = document.activeElement;
        this.modal.hidden = false;
        this.isOpen = true;

        if (this.isDocked()) {
          if (window.matchMedia(DESKTOP_MQ).matches) {
            document.addEventListener('click', this.handleOutsideClick, true);
          }
        } else {
          document.body.classList.add('pvg-no-scroll');
        }
      }

      this.load(index);

      var closeBtn = this.modal.querySelector('[data-pvg-close].pvg-modal__ctrl');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
    }

    load(index) {
      var card = this.cards[index];
      if (!card) return;

      this.currentIndex = index;
      if (this.titleEl) this.titleEl.textContent = card.dataset.title || '';

      this.stopPlayback();
      this.hideError();
      this.embedPaused = false;
      if (this.pausedEl) this.pausedEl.hidden = true;
      this.setSoundIcon(this.startMuted);

      if (card.dataset.type === 'vimeo') {
        this.playVimeo(card);
      } else if (card.dataset.type === 'embed') {
        this.playEmbed(card);
      } else {
        this.playFile(card.dataset.src);
      }
    }

    /* ------------------------------ Sound / tap --------------------------- */

    setSoundIcon(muted) {
      this.isMutedNow = !!muted;
      if (this.soundOnEl) this.soundOnEl.hidden = this.isMutedNow;
      if (this.soundOffEl) this.soundOffEl.hidden = !this.isMutedNow;
    }

    ytCommand(func) {
      var iframe = this.embedEl ? this.embedEl.querySelector('iframe') : null;
      if (!iframe || !iframe.contentWindow) return;
      iframe.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: func, args: [] }),
        '*'
      );
    }

    toggleSound() {
      var card = this.cards[this.currentIndex];
      if (!card) return;
      var next = !this.isMutedNow;

      if (card.dataset.type === 'vimeo' && this.vimeoPlayer) {
        this.vimeoPlayer.setMuted(next).catch(function () {});
      } else if (card.dataset.type === 'embed') {
        this.ytCommand(next ? 'mute' : 'unMute');
      } else if (this.videoEl) {
        this.videoEl.muted = next; // volumechange listener syncs the icon
        return;
      }
      this.setSoundIcon(next);
    }

    togglePlay() {
      var self = this;
      var card = this.cards[this.currentIndex];
      if (!card) return;

      if (card.dataset.type === 'vimeo' && this.vimeoPlayer) {
        this.vimeoPlayer.getPaused().then(function (paused) {
          if (paused) {
            self.vimeoPlayer.play().catch(function () {});
            if (self.pausedEl) self.pausedEl.hidden = true;
          } else {
            self.vimeoPlayer.pause().catch(function () {});
            if (self.pausedEl) self.pausedEl.hidden = false;
          }
        });
      } else if (card.dataset.type === 'embed') {
        this.embedPaused = !this.embedPaused;
        this.ytCommand(this.embedPaused ? 'pauseVideo' : 'playVideo');
        if (this.pausedEl) this.pausedEl.hidden = !this.embedPaused;
      } else if (this.videoEl && !this.videoEl.hidden) {
        if (this.videoEl.paused) {
          this.videoEl.play().catch(function () {});
        } else {
          this.videoEl.pause();
        }
      }
    }

    /* ------------------------------ Players ------------------------------ */

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    playVimeo(card) {
      var self = this;

      this.videoEl.hidden = true;
      this.embedEl.hidden = false;

      var iframe = document.createElement('iframe');
      iframe.setAttribute('title', card.dataset.title || 'Video');
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute(
        'allow',
        'autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media'
      );
      iframe.setAttribute('allowfullscreen', '');
            iframe.src = buildVimeoUrl(card.dataset.pvgVimeoId, card.dataset.pvgVimeoHash, this.startMuted);

      this.embedEl.appendChild(iframe);

      loadVimeoSdk()
        .then(function () {
          if (!self.embedEl.contains(iframe)) return;

          var player = new window.Vimeo.Player(iframe);
          self.vimeoPlayer = player;

          player.on('ended', function () {
            if (self.cards.length > 1) self.step(1);
          });

          player.play().catch(function () {
            player
              .setMuted(true)
              .then(function () {
                self.setSoundIcon(true);
                return player.play();
              })
              .catch(function () {});
          });
        })
        .catch(function () {
          /* No SDK: the plain iframe still plays; tap/auto-advance degrade. */
        });
    }

    playEmbed(card) {
      this.videoEl.hidden = true;
      this.embedEl.hidden = false;

      var iframe = document.createElement('iframe');
      iframe.setAttribute('title', card.dataset.title || 'Video');
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
      );
      iframe.setAttribute('allowfullscreen', '');
      iframe.src = buildGenericEmbedUrl(card.dataset.src, this.startMuted);

      this.embedEl.appendChild(iframe);
    }

    playFile(src) {
      var self = this;

      this.embedEl.hidden = true;
      this.videoEl.hidden = false;
      this.videoEl.muted = this.startMuted;
      this.videoEl.setAttribute('src', src);
      this.videoEl.load();

      var attempt = this.videoEl.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          self.videoEl.muted = true;
          var retry = self.videoEl.play();
          if (retry && typeof retry.catch === 'function') retry.catch(function () {});
        });
      }
    }

    stopPlayback() {
      if (this.vimeoPlayer) {
        try {
          this.vimeoPlayer.destroy();
        } catch (e) {
          /* noop */
        }
        this.vimeoPlayer = null;
      }

      if (this.embedEl) {
        this.embedEl.innerHTML = '';
        this.embedEl.hidden = true;
      }

      if (this.videoEl) {
        try {
          this.videoEl.pause();
        } catch (e) {
          /* noop */
        }
        this.videoEl.removeAttribute('src');
        this.videoEl.load();
        this.videoEl.hidden = true;
      }
    }

    close() {
      if (!this.isOpen) return;

      this.stopPlayback();
      this.hideError();
      this.modal.hidden = true;
      this.isOpen = false;
      document.body.classList.remove('pvg-no-scroll');
      document.removeEventListener('click', this.handleOutsideClick, true);

      var target = this.cards[this.currentIndex] || this.lastFocused;
      if (target && typeof target.focus === 'function') {
        target.focus({ preventScroll: true });
      }
    }
  }

  customElements.define('product-video-gallery', ProductVideoGallery);
})();